import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Activity, KeyRound, Loader2, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const SECRET_LABELS = {
  META_APP_ID: "Meta App ID", META_APP_SECRET: "Meta App Secret",
  META_CONFIG_ID: "Meta Config ID (Embedded Signup)",
  WHATSAPP_VERIFY_TOKEN: "Verify Token do webhook",
};
const CONN_STATUS = {
  connected: ["Conectado", "text-emerald-400"], disconnected: ["Desconectado", "text-zinc-500"],
  token_expired: ["Erro", "text-rose-400"], pending: ["Pendente", "text-amber-400"],
  error: ["Erro", "text-rose-400"],
};
const CHECK_STATUS = {
  ok: ["🟢 Operacional", "text-emerald-400"],
  atencao: ["🟡 Atenção", "text-amber-400"],
  erro: ["🔴 Erro", "text-rose-400"],
};

export default function AdminWhatsApp() {
  const [status, setStatus] = useState(null);
  const [connTest, setConnTest] = useState(null);
  const [connLoading, setConnLoading] = useState(false);
  const [form, setForm] = useState({ meta_app_id: "", meta_app_secret: "", meta_config_id: "", whatsapp_verify_token: "" });
  const [official, setOfficial] = useState({ official_token: "", official_waba_id: "", official_phone_number_id: "", official_phone: "" });

  const load = () => api.get("/admin/meta/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveSecrets = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/meta/secrets", form);
      toast.success("Credenciais da Meta App atualizadas");
      setForm({ meta_app_id: "", meta_app_secret: "", meta_config_id: "", whatsapp_verify_token: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveOfficial = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/meta/secrets", official);
      toast.success("Número oficial do RAVI atualizado");
      setOfficial({ official_token: "", official_waba_id: "", official_phone_number_id: "", official_phone: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const runConnectivity = async () => {
    setConnLoading(true);
    try {
      const { data } = await api.post("/admin/meta/connectivity-test");
      setConnTest(data);
    } catch (err) {
      toast.error(apiError(err, "Falha ao executar o teste de conectividade"));
    } finally {
      setConnLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-5xl" data-testid="admin-whatsapp-page">
      <div className="fade-up">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-100">WhatsApp / Meta</h1>
        <p className="text-sm text-zinc-500 mt-1">Uma Meta App do RAVI para todos os escritórios. Secrets são write-only — nunca exibidos após salvar.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 fade-up">
        <div className="rounded-xl border border-[#23283E] bg-[#0F111A] p-5 space-y-3" data-testid="meta-app-card">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-zinc-200 text-sm">Meta App do RAVI</h3>
            <Badge className={status?.ambiente === "produção"
              ? "bg-emerald-950/60 border border-emerald-700/50 text-emerald-400"
              : "bg-amber-950/60 border border-amber-700/50 text-amber-400"} data-testid="meta-ambiente-badge">
              {status?.ambiente || "…"}
            </Badge>
          </div>
          {status?.app_id_masked && <p className="text-xs text-zinc-400">App ID: <span className="font-mono-code">{status.app_id_masked}</span></p>}
          <div className="flex items-center justify-between rounded-lg border border-[#23283E] bg-[#090A0F] px-3 py-2" data-testid="verify-token-status">
            <div>
              <p className="text-xs text-zinc-300">Verify Token do Webhook</p>
              <p className="font-mono-code text-[11px] text-zinc-600">●●●●●●●●</p>
            </div>
            {status?.secrets?.WHATSAPP_VERIFY_TOKEN
              ? <span className="text-[11px] text-emerald-400">Status: Configurado</span>
              : <span className="text-[11px] text-amber-400">Status: Não configurado</span>}
          </div>
          <div className="space-y-2 pt-1">
            {Object.entries(SECRET_LABELS).map(([k, label]) => (
              <div key={k} className="flex items-center justify-between text-xs" data-testid={`secret-${k}`}>
                <span className="text-zinc-400">{label}</span>
                {status?.secrets?.[k]
                  ? <span className="text-emerald-400">Configurado</span>
                  : <span className="text-amber-400">Não configurado</span>}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-[#23283E] bg-[#0F111A] p-5 space-y-3" data-testid="secrets-form-card">
          <h3 className="font-display font-semibold text-zinc-200 text-sm flex items-center gap-2">
            <KeyRound size={14} className="text-zinc-500" /> Credenciais da Meta App
          </h3>
          <p className="text-[11px] text-zinc-600">Os valores nunca são exibidos novamente após salvar.</p>
          <form onSubmit={saveSecrets} className="space-y-2.5">
            {[["meta_app_id", "Meta App ID"], ["meta_app_secret", "Meta App Secret"],
              ["meta_config_id", "Config ID (Embedded Signup)"],
              ["whatsapp_verify_token", "Verify Token do Webhook (rotacionar)"]].map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-zinc-500 text-[11px]">{label}</Label>
                <Input type="password" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`secret-input-${k}`} placeholder="••••••••" className="bg-[#090A0F] border-[#23283E] h-8 text-xs font-mono-code" />
              </div>
            ))}
            <Button type="submit" data-testid="secrets-save-btn" className="w-full brand-gradient brand-gradient-hover text-white border-0">
              Atualizar
            </Button>
          </form>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 fade-up">
        <div className="rounded-xl border border-[#23283E] bg-[#0F111A] p-5 space-y-3" data-testid="official-number-card">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-zinc-200 text-sm flex items-center gap-2">
              <PhoneCall size={14} className="text-zinc-500" /> Número oficial do RAVI
            </h3>
            {status?.official?.configurado
              ? <Badge className="bg-emerald-950/60 border border-emerald-700/50 text-emerald-400" data-testid="official-configured-badge">Configurado</Badge>
              : <Badge className="bg-amber-950/60 border border-amber-700/50 text-amber-400" data-testid="official-pending-badge">Pendente</Badge>}
          </div>
          {status?.official?.configurado ? (
            <div className="text-xs space-y-1.5" data-testid="official-summary">
              <p className="text-zinc-300">Número de atendimento: <span className="font-mono-code text-zinc-100">{status.official.phone || "—"}</span></p>
              <p className="text-zinc-500">WABA ID: <span className="font-mono-code">{status.official.waba_id_masked}</span> • Phone Number ID: <span className="font-mono-code">{status.official.phone_number_id_masked}</span></p>
              <p className="text-zinc-500">Token de acesso: <span className="text-emerald-400">armazenado com criptografia</span></p>
            </div>
          ) : (
            <p className="text-xs text-zinc-500">Número oficial de atendimento ainda não configurado.</p>
          )}
        </div>

        <div className="rounded-xl border border-[#23283E] bg-[#0F111A] p-5 space-y-3" data-testid="official-form-card">
          <h3 className="font-display font-semibold text-zinc-200 text-sm">Atualizar número oficial</h3>
          <p className="text-[11px] text-zinc-600">Infraestrutura do RAVI — o advogado nunca vê nem informa estes dados.</p>
          <form onSubmit={saveOfficial} className="space-y-2.5">
            {[["official_token", "Token de acesso (permanente)"], ["official_waba_id", "WABA ID"],
              ["official_phone_number_id", "Phone Number ID"], ["official_phone", "Número exibido (ex: +55 11 97853-6185)"]].map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-zinc-500 text-[11px]">{label}</Label>
                <Input type="password" value={official[k]} onChange={(e) => setOfficial({ ...official, [k]: e.target.value })}
                  data-testid={`official-input-${k}`} placeholder="••••••••" className="bg-[#090A0F] border-[#23283E] h-8 text-xs font-mono-code" />
              </div>
            ))}
            <Button type="submit" data-testid="official-save-btn" className="w-full brand-gradient brand-gradient-hover text-white border-0">
              Salvar número oficial
            </Button>
          </form>
        </div>
      </div>

      <div className="rounded-xl border border-[#23283E] bg-[#0F111A] p-5 fade-up" data-testid="integration-status-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-zinc-200 text-sm">Status da integração</h3>
            <p className="text-[11px] text-zinc-600 mt-0.5">Cada execução fica registrada no log administrativo com data/hora e resultado.</p>
          </div>
          <Button onClick={runConnectivity} disabled={connLoading} size="sm" data-testid="run-connectivity-test-btn"
            className="brand-gradient brand-gradient-hover text-white border-0">
            {connLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Activity size={13} className="mr-1.5" />}
            Executar teste de conectividade
          </Button>
        </div>
        {!connTest && <p className="text-xs text-zinc-600">Execute o teste para verificar Meta API, credenciais, webhook, mensagens e banco.</p>}
        {connTest && (
          <div className="grid md:grid-cols-2 gap-2.5" data-testid="connectivity-results">
            {Object.entries(connTest.checks).map(([k, c]) => (
              <div key={k} className="rounded-lg border border-[#23283E] bg-[#090A0F] px-3.5 py-3" data-testid={`check-${k}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-zinc-200">{c.label}</p>
                  <span className={`text-[11px] ${(CHECK_STATUS[c.status] || CHECK_STATUS.erro)[1]}`}>
                    {(CHECK_STATUS[c.status] || CHECK_STATUS.erro)[0]}
                  </span>
                </div>
                <p className="text-[11px] text-zinc-500 mt-1">{c.mensagem}</p>
              </div>
            ))}
            <p className="md:col-span-2 text-[10px] text-zinc-600">Executado em {new Date(connTest.executado_em).toLocaleString("pt-BR")}</p>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-[#23283E] bg-[#0F111A] p-5 fade-up" data-testid="connections-card">
        <h3 className="font-display font-semibold text-zinc-200 text-sm mb-4">Conexões por escritório</h3>
        {(status?.connections || []).length === 0 && <p className="text-xs text-zinc-600">Nenhuma conexão ainda.</p>}
        <div className="space-y-2">
          {(status?.connections || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-lg border border-[#23283E] bg-[#090A0F] px-3.5 py-2.5" data-testid={`conn-${c.id}`}>
              <div>
                <p className="text-sm text-zinc-200 flex items-center gap-2">
                  {c.office_name}
                  <Badge className={c.tipo === "plataforma"
                    ? "bg-indigo-950/60 border border-indigo-700/50 text-indigo-300 text-[9px]"
                    : "bg-zinc-800 border border-zinc-700 text-zinc-400 text-[9px]"}>
                    {c.tipo === "plataforma" ? "Plataforma" : "Escritório"}
                  </Badge>
                </p>
                <p className="text-xs text-zinc-500">{c.display_phone_number || "—"}</p>
              </div>
              <span className={`text-xs ${(CONN_STATUS[c.status] || CONN_STATUS.pending)[1]}`}>
                {(CONN_STATUS[c.status] || CONN_STATUS.pending)[0]}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
